import { Plus, Trash2 } from 'lucide-react';
import type { Rubric, RubricCriterion } from '../../types';

interface Props {
  rubric: Rubric;
  onChange: (r: Rubric) => void;
}

export default function RubricBuilder({ rubric, onChange }: Props) {
  const totalWeight = rubric.criteria.reduce((a, c) => a + c.weight, 0);

  const update = (idx: number, patch: Partial<RubricCriterion>) => {
    const next = { ...rubric, criteria: rubric.criteria.map((c, i) => (i === idx ? { ...c, ...patch } : c)) };
    onChange(next);
  };
  const add = () => onChange({ ...rubric, criteria: [...rubric.criteria, blankCriterion()] });
  const remove = (idx: number) => onChange({ ...rubric, criteria: rubric.criteria.filter((_, i) => i !== idx) });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <label className="label">Rubric name</label>
          <input className="input" value={rubric.name} onChange={(e) => onChange({ ...rubric, name: e.target.value })} />
        </div>
        <div>
          <label className="label">Scale max</label>
          <input type="number" className="input" value={rubric.scaleMax}
            onChange={(e) => onChange({ ...rubric, scaleMax: Number(e.target.value) })} />
        </div>
      </div>

      <div className="card">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 text-xs font-medium uppercase tracking-wide text-ink-500 border-b border-ink-100">
          <div className="col-span-4">Criterion</div>
          <div className="col-span-5">Description</div>
          <div className="col-span-1 text-right">Max</div>
          <div className="col-span-1 text-right">Weight %</div>
          <div className="col-span-1" />
        </div>
        <div className="divide-y divide-ink-100">
          {rubric.criteria.map((c, i) => (
            <div key={c.id} className="grid grid-cols-12 gap-2 items-center px-4 py-3">
              <input className="input col-span-4" value={c.name}
                onChange={(e) => update(i, { name: e.target.value })} placeholder="e.g. Depth of analysis" />
              <input className="input col-span-5" value={c.description ?? ''}
                onChange={(e) => update(i, { description: e.target.value })} placeholder="What excellent work looks like…" />
              <input type="number" className="input col-span-1 text-right" value={c.max}
                onChange={(e) => update(i, { max: Number(e.target.value) })} />
              <input type="number" className="input col-span-1 text-right" value={c.weight}
                onChange={(e) => update(i, { weight: Number(e.target.value) })} />
              <button onClick={() => remove(i)} className="col-span-1 justify-self-end p-2 rounded-lg hover:bg-red-50 text-ink-400 hover:text-red-600"
                aria-label="Remove">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="px-4 py-3 flex items-center justify-between border-t border-ink-100">
          <button onClick={add} className="btn-ghost btn-sm"><Plus className="w-4 h-4" /> Add criterion</button>
          <span className={`text-sm ${totalWeight === 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
            Total weight: {totalWeight}% {totalWeight !== 100 && '(should be 100%)'}
          </span>
        </div>
      </div>
    </div>
  );
}

export function blankCriterion(): RubricCriterion {
  return { id: crypto.randomUUID(), name: '', description: '', weight: 0, max: 10 };
}

export function defaultRubric(): Rubric {
  return {
    name: 'Default academic rubric',
    scaleMin: 0,
    scaleMax: 100,
    criteria: [
      { id: crypto.randomUUID(), name: 'Structure & organization',  description: 'Logical flow, clear sections.', weight: 15, max: 10 },
      { id: crypto.randomUUID(), name: 'Clarity of writing',        description: 'Unambiguous, concise prose.',    weight: 15, max: 10 },
      { id: crypto.randomUUID(), name: 'Depth of analysis',         description: 'Beyond surface treatment.',      weight: 20, max: 10 },
      { id: crypto.randomUUID(), name: 'Evidence & citations',      description: 'Cited primary sources.',         weight: 20, max: 10 },
      { id: crypto.randomUUID(), name: 'Originality',               description: 'Own contributions stand out.',   weight: 10, max: 10 },
      { id: crypto.randomUUID(), name: 'Mechanics',                 description: 'Grammar, spelling, formatting.', weight: 10, max: 10 },
      { id: crypto.randomUUID(), name: 'Presentation',              description: 'Typography, figures, headings.', weight: 10, max: 10 },
    ],
  };
}
